import { useState, type ReactNode } from 'react';
import { api, ApiError } from '../api/client';
import { useAuth } from '../state/auth';
import { Button, Card, Field, Input, Spinner } from './ui';

export function AuthGate({ children }: { children: ReactNode }) {
  const { status, loading, refresh } = useAuth();

  if (loading) return <Centered><Spinner /></Centered>;
  if (!status) return <Centered><Card>Cannot reach the server.</Card></Centered>;

  if (!status.setupComplete) return <Centered><SetupForm onDone={refresh} /></Centered>;
  if (!status.authenticated) return <Centered><LoginForm onDone={refresh} /></Centered>;
  return <>{children}</>;
}

function Centered({ children }: { children: ReactNode }) {
  return <div className="flex min-h-screen items-center justify-center p-4">{children}</div>;
}

function SetupForm({ onDone }: { onDone: () => void }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setErr('');
    try {
      await api.post('/api/auth/setup', { username, password });
      onDone();
    } catch (e2) {
      setErr(e2 instanceof ApiError ? e2.message : 'Setup failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="w-full max-w-sm space-y-4">
      <div>
        <h1 className="text-lg font-semibold">Welcome to jelly-dl</h1>
        <p className="text-sm text-slate-400">Create your admin account to get started.</p>
      </div>
      <form className="space-y-3" onSubmit={submit}>
        <Field label="Username">
          <Input value={username} onChange={(e) => setUsername(e.target.value)} autoFocus />
        </Field>
        <Field label="Password" hint="At least 8 characters">
          <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} />
        </Field>
        {err && <p className="text-sm text-rose-400">{err}</p>}
        <Button type="submit" disabled={busy} className="w-full">Create account</Button>
      </form>
    </Card>
  );
}

function LoginForm({ onDone }: { onDone: () => void }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setErr('');
    try {
      await api.post('/api/auth/login', { username, password });
      onDone();
    } catch (e2) {
      setErr(e2 instanceof ApiError ? e2.message : 'Login failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="w-full max-w-sm space-y-4">
      <h1 className="text-lg font-semibold">Sign in</h1>
      <form className="space-y-3" onSubmit={submit}>
        <Field label="Username">
          <Input value={username} onChange={(e) => setUsername(e.target.value)} autoFocus />
        </Field>
        <Field label="Password">
          <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} />
        </Field>
        {err && <p className="text-sm text-rose-400">{err}</p>}
        <Button type="submit" disabled={busy} className="w-full">Sign in</Button>
      </form>
    </Card>
  );
}
